import React, { useState, useEffect, useCallback } from 'react';
import { siteInventoryService } from '../services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Trash2, Save, X, FileSpreadsheet, Eye, ClipboardList } from 'lucide-react';
import { getApiError } from '@/lib/utils';

export default function SiteSheetsTab({ siteId }) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ template_name: '', sort_method: 'product', is_default: false, lines: [] });

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [generating, setGenerating] = useState(false);

  const loadData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [tmpl, inv, locs] = await Promise.all([
        siteInventoryService.listTemplates(siteId),
        siteInventoryService.listInventory(siteId),
        siteInventoryService.listLocations(siteId),
      ]);
      setTemplates(tmpl || []);
      setInventory(inv || []);
      setLocations(locs || []);
    } catch (err) {
      console.error('Failed to load sheets data:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditingTemplate(null);
    // Auto-populate lines from site inventory
    const lines = inventory
      .filter(i => i.active_for_counting)
      .map((i, idx) => ({
        beverage_product_id: i.beverage_product_id,
        product_name: i.product_name,
        beverage_location_id: i.default_location_id || null,
        display_sequence: idx,
      }));
    setForm({ template_name: '', sort_method: 'product', is_default: templates.length === 0, lines });
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditingTemplate(tmpl);
    setForm({
      template_name: tmpl.template_name,
      sort_method: tmpl.sort_method,
      is_default: tmpl.is_default,
      lines: (tmpl.lines || []).map(l => ({
        beverage_product_id: l.beverage_product_id,
        product_name: l.product_name,
        beverage_location_id: l.beverage_location_id,
        display_sequence: l.display_sequence,
      })),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.template_name.trim()) {
      toast({ title: 'Validation', description: 'Template name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        template_name: form.template_name,
        sort_method: form.sort_method,
        is_default: form.is_default,
        lines: form.lines.map((l, idx) => ({
          beverage_product_id: l.beverage_product_id,
          beverage_location_id: l.beverage_location_id || null,
          display_sequence: idx,
        })),
      };
      if (editingTemplate) {
        await siteInventoryService.updateTemplate(siteId, editingTemplate.id, payload);
        toast({ title: 'Updated', description: 'Template updated' });
      } else {
        await siteInventoryService.createTemplate(siteId, payload);
        toast({ title: 'Created', description: 'Template created' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tmpl) => {
    if (!window.confirm(`Delete template "${tmpl.template_name}"?`)) return;
    try {
      await siteInventoryService.deleteTemplate(siteId, tmpl.id);
      toast({ title: 'Deleted', description: 'Template removed' });
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleGenerate = async (tmpl) => {
    setGenerating(true);
    try {
      const data = await siteInventoryService.generateSheet(siteId, tmpl.id);
      setPreviewData(data);
      setPreviewOpen(true);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to generate sheet', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const removeLine = (idx) => {
    setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };

  const updateLineLocation = (idx, locId) => {
    setForm(f => ({
      ...f,
      lines: f.lines.map((l, i) => i === idx ? { ...l, beverage_location_id: locId === 'none' ? null : parseInt(locId) } : l),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="sheets-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-sheets-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={openCreate} disabled={inventory.length === 0} data-testid="create-template-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Create Template
        </Button>
      </div>

      {inventory.length === 0 && (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            Add products to this site's inventory first before creating count sheet templates.
          </CardContent>
        </Card>
      )}

      {templates.length === 0 && inventory.length > 0 ? (
        <div className="text-center py-10 text-muted-foreground" data-testid="sheets-empty">
          <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No count sheet templates yet</p>
          <p className="text-xs mt-1">Create a template to generate structured stock count sheets</p>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map(tmpl => (
            <Card key={tmpl.id} data-testid={`template-card-${tmpl.id}`}>
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{tmpl.template_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {tmpl.line_count} item{tmpl.line_count !== 1 ? 's' : ''} · Sort: {tmpl.sort_method}
                    </p>
                  </div>
                  {tmpl.is_default && <Badge variant="secondary" className="text-[10px]">Default</Badge>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleGenerate(tmpl)} disabled={generating}
                    data-testid={`generate-sheet-${tmpl.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> Generate
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(tmpl)}
                    data-testid={`edit-template-${tmpl.id}`}>
                    <ClipboardList className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(tmpl)}
                    data-testid={`delete-template-${tmpl.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Template Dialog ──────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-4 py-2 pr-1" style={{ maxHeight: 'calc(85vh - 140px)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Template Name *</Label>
                <Input value={form.template_name} onChange={(e) => setForm(f => ({ ...f, template_name: e.target.value }))}
                  placeholder="e.g. Full Bar Count" data-testid="input-template-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Sort Method</Label>
                <Select value={form.sort_method} onValueChange={(v) => setForm(f => ({ ...f, sort_method: v }))}>
                  <SelectTrigger data-testid="select-sort-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">By Product</SelectItem>
                    <SelectItem value="category">By Category</SelectItem>
                    <SelectItem value="location">By Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_default} onCheckedChange={(v) => setForm(f => ({ ...f, is_default: v }))}
                data-testid="template-default-switch" />
              <Label className="text-sm">Default template for this site</Label>
            </div>

            {/* Lines */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Products ({form.lines.length})</Label>
              {form.lines.length === 0 ? (
                <p className="text-sm text-muted-foreground py-3 text-center">No products in template</p>
              ) : (
                <div className="border rounded-md max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="text-xs">Product</TableHead>
                        {locations.length > 0 && <TableHead className="text-xs">Location</TableHead>}
                        <TableHead className="text-xs w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.lines.map((line, idx) => (
                        <TableRow key={idx} data-testid={`tmpl-line-${idx}`}>
                          <TableCell className="py-1.5 text-sm">{line.product_name || `Product #${line.beverage_product_id}`}</TableCell>
                          {locations.length > 0 && (
                            <TableCell className="py-1.5">
                              <Select value={line.beverage_location_id ? String(line.beverage_location_id) : 'none'}
                                onValueChange={(v) => updateLineLocation(idx, v)}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">—</SelectItem>
                                  {locations.filter(l => l.is_active).map(l => (
                                    <SelectItem key={l.id} value={String(l.id)}>{l.location_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          )}
                          <TableCell className="py-1.5">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLine(idx)}>
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t pt-3 gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}><X className="h-4 w-4 mr-1.5" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Generated Sheet Preview ──────────────────────────── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="flex flex-col dialog-resizable"
          style={{ width: '42rem', maxHeight: '85vh' }}
          data-testid="sheet-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>Count Sheet: {previewData?.template_name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 py-2">
            {previewData?.sections?.map((section, sIdx) => (
              <div key={sIdx} className="mb-4">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Badge variant="outline">{section.group_name}</Badge>
                  <span className="text-xs text-muted-foreground">{section.items.length} item{section.items.length !== 1 ? 's' : ''}</span>
                </h4>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="text-xs w-[40px]">#</TableHead>
                        <TableHead className="text-xs">Product</TableHead>
                        <TableHead className="text-xs">Code</TableHead>
                        {previewData.sort_method !== 'location' && <TableHead className="text-xs">Location</TableHead>}
                        <TableHead className="text-xs text-center w-[80px]">Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {section.items.map((item, iIdx) => (
                        <TableRow key={iIdx}>
                          <TableCell className="text-xs text-muted-foreground">{iIdx + 1}</TableCell>
                          <TableCell className="text-sm font-medium">{item.product_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{item.product_code || '—'}</TableCell>
                          {previewData.sort_method !== 'location' && (
                            <TableCell className="text-xs">{item.location || '—'}</TableCell>
                          )}
                          <TableCell className="text-center"><div className="border-b border-dashed w-12 mx-auto" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
            {previewData && (
              <p className="text-xs text-muted-foreground mt-3 text-right">
                {previewData.total_lines} total items · Generated {new Date(previewData.generated_at).toLocaleString()}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
